import Button from '@risksmart-app/components/src/button';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import _ from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_COL_WIDTH } from 'src/App.config';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { Permission } from 'src/rbac/Permission';

import Link from '@/components/link';
import type { UseGetTablePropsOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { friendlyIdColumn } from '@/utils/table/utils/friendlyIdColumn';
import { indicatorDetailsUrl } from '@/utils/urls';

import { conformanceRatingFromResults } from '../calculateConformanceRating';
import { latestResultValueFromData } from '../latestResultValueFromData';
import type { IndicatorFlatFields, IndicatorTableFields } from './types';
import { useLabelledFields } from './useLabelledFields';

const useGetFieldConfig = (): TableFields<IndicatorTableFields> => {
  const { getByValue: statusGetByValue } = useRating(
    'indicator_conformance_status'
  );

  const { t: stc } = useTranslation(['common'], { keyPrefix: 'columns' });
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'indicators.columns',
  });

  return useMemo<TableFields<IndicatorTableFields>>(
    () => ({
      SequentialIdLabel: friendlyIdColumn<IndicatorTableFields>({
        header: stc('id'),
        parentType: ParentTypes.Indicator,
        hrefFor: (item) => indicatorDetailsUrl(item.Id),
      }),
      Title: {
        formId: 'indicator',
        fieldId: 'Title',
        cell: (item) => (
          <Link variant={'secondary'} href={item.Id}>
            {item.Title}
          </Link>
        ),
        maxWidth: MAX_COL_WIDTH,
        isRowHeader: true,
      },
      TestFrequencyLabelled: {
        header: st('test_frequency'),
      },
      LatestResultValue: {
        header: st('latest_result'),
        cell: (item) => {
          const result = latestResultValueFromData(item);
          if (_.isNil(result) || result === '') {
            return '-';
          }
          if (item.Unit) {
            return `${result} ${item.Unit}`;
          }

          return result;
        },
      },
      ConformanceLabelled: {
        header: st('conformance'),
        cell: (item: IndicatorTableFields) => {
          const rating = conformanceRatingFromResults(item);

          return <SimpleRatingBadge rating={statusGetByValue(rating)} />;
        },
      },

      LatestResultDate: dateColumnFromConfig({
        header: { header: st('latest_result_date') },
        dateField: 'LatestResultDate',
      }),
    }),
    [st, stc, statusGetByValue]
  );
};

const useGetIndicatorTableProps = (
  records: IndicatorFlatFields[] | undefined,
  handleCreateOpen: () => void,
  parentObject: ObjectWithContributors
): UseGetTablePropsOptions<IndicatorTableFields> => {
  const { t: stc } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'indicators' });
  const fields = useGetFieldConfig();
  const labelledFields = useLabelledFields(records);

  return useMemo(
    () => ({
      tableId: 'indicatorTab',
      data: labelledFields,
      customAttributeFormIds: ['indicator'],
      entityLabel: stc('indicators.entity_name'),
      emptyCollectionAction: (
        <Permission permission={'insert:indicator'} parentObject={parentObject}>
          <Button formAction={'none'} onClick={handleCreateOpen}>
            {st('create_new_button')}
          </Button>
        </Permission>
      ),
      preferencesStorageKey: 'IndicatorsRegisterTable-PreferencesV1',
      enableFiltering: true,
      defaultSortingState: {
        sortingColumn: 'LatestResultDate',
        sortingDirection: 'desc',
      },
      initialColumns: [
        'Title',
        'TestFrequencyLabelled',
        'LatestResultValue',
        'ConformanceLabelled',
        'LatestResultDate',
      ],
      fields,
    }),
    [fields, labelledFields, stc, handleCreateOpen, st, parentObject]
  );
};

export const useGetCollectionTableProps = (
  records: IndicatorFlatFields[] | undefined,
  handleCreateOpen: () => void,
  parentObject: ObjectWithContributors
): TablePropsWithActions<IndicatorTableFields> => {
  const props = useGetIndicatorTableProps(
    records,
    handleCreateOpen,
    parentObject
  );

  return useGetTableProps(props);
};
